import { LDate } from '@app/components/date/LDate';
import { isTodayInTimezone, isYesterdayInTimezone } from '@app/components/date/ldate-utils';
import { LTime } from '@app/components/date/LTime';
import { useTimezone } from '@app/core/hooks/useTimezone';
import { Box } from '@mui/material';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

/**
 * Wraps label + time in a single inline-flex row. Used to avoid duplicating
 * the same Box structure for today/yesterday and full-date variants.
 */
function TimeWithLabel({ gap = 0.5, children }: { gap?: number; children: ReactNode }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap }}>
      {children}
    </Box>
  );
}

/**
 * Fragment start time: HH:mm if today/yesterday, else weekday + DD/MM + HH:mm.
 * Calendar day and wall clock use the station timezone (not the browser).
 */
export function FragmentCardTime({
  from,
  showRecentDate = false,
  dateFormat = 'short',
}: {
  from: string;
  showRecentDate?: boolean;
  dateFormat?: 'short' | 'long';
}) {
  const tz = useTimezone();
  const isToday = isTodayInTimezone(from, tz);
  const isYesterday = isYesterdayInTimezone(from, tz);
  const isRecent = isToday || isYesterday;
  const showDate = !isRecent || (showRecentDate && isRecent);

  const timePart = <LTime value={from} hour12={false} tz={tz} />;

  if (!showDate) {
    return timePart;
  }

  let datePart: ReactNode;

  if (isToday) {
    datePart = <FormattedMessage id="fragments.listV3.section.today" defaultMessage="Hoy" />;
  } else if (isYesterday) {
    datePart = <FormattedMessage id="fragments.listV3.section.yesterday" defaultMessage="Ayer" />;
  } else {
    datePart = (
      <>
        <LDate value={from} weekday={dateFormat} capitalize tz={tz} />{' '}
        <LDate value={from} day="2-digit" month="2-digit" tz={tz} />
      </>
    );
  }

  return (
    <TimeWithLabel gap={0.75}>
      <span>{datePart}</span>
      <span>{timePart}</span>
    </TimeWithLabel>
  );
}
